import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../components/Layout'
import { 
  CogIcon,
  BuildingStorefrontIcon,
  TrophyIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  MapPinIcon
} from '@heroicons/react/24/outline'
import { useRecycling } from '../contexts/RecyclingContext'
import CarbonCreditLedger from '../components/CarbonCreditLedger'

const AdminPortal: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('month')
  const [ledgerSummary, setLedgerSummary] = useState({
    totalCredits: 0,
    verifiedCredits: 0,
    pendingCredits: 0,
    totalRecords: 0
  })

  const brandPerformanceData = [
    {
      brand: 'OneChain Partner',
      total_bottles: 45680,
      percentage: 24.8,
      avg_daily: 1522,
      popular_sizes: ['330ml', '500ml', '1L'],
      growth: '+12.3%'
    },
    {
      brand: 'Coca-Cola',
      total_bottles: 39540,
      percentage: 21.5,
      avg_daily: 1318,
      popular_sizes: ['600ml', '330ml', '1L'],
      growth: '+15.2%'
    },
    {
      brand: 'Vita',
      total_bottles: 29420,
      percentage: 16.0,
      avg_daily: 980,
      popular_sizes: ['500ml', '250ml'],
      growth: '+8.7%'
    },
    {
      brand: 'Bonaqua',
      total_bottles: 22180,
      percentage: 12.0,
      avg_daily: 739,
      popular_sizes: ['500ml', '600ml'],
      growth: '+6.1%'
    },
    {
      brand: 'Sprite',
      total_bottles: 18750,
      percentage: 10.2,
      avg_daily: 625,
      popular_sizes: ['330ml', '500ml'],
      growth: '+4.8%'
    },
    {
      brand: 'Others',
      total_bottles: 28680,
      percentage: 15.5,
      avg_daily: 956,
      popular_sizes: ['Various'],
      growth: '+7.3%'
    }
  ]

  const systemStats = [
    {
      name: 'Total Brand Returns',
      value: '184,250',
      change: '+2,345',
      changeType: 'increase',
      icon: BuildingStorefrontIcon
    },
    {
      name: 'Active Brands',
      value: '47',
      change: '+3',
      changeType: 'increase',
      icon: TrophyIcon
    },
    {
      name: 'RVM Network',
      value: '598',
      change: '+12',
      changeType: 'increase',
      icon: CogIcon
    },
    {
      name: 'Daily Processing',
      value: '6,141',
      change: '+78',
      changeType: 'increase',
      icon: CalendarDaysIcon
    }
  ]

  const { getAllTotals, getDistrictTotals } = useRecycling()
  const totals = getAllTotals()
  const districts = getDistrictTotals().slice(0,5)

  return (
    <Layout portalType="admin">
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Brand Performance Analytics</h1>
              <p className="text-gray-600">Monitor bottle return data across multiple brands and products</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/planning"
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
              >
                RVM Intelligences
              </Link>
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
            </div>
          </div>

          {/* System Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {systemStats.map((stat) => (
              <div key={stat.name} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <stat.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <div className="flex items-baseline">
                      <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                      <p className={`ml-2 text-sm font-medium ${
                        stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.change}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Plastic Credits & District Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
            {/* Plastic Credit Overview - Enhanced */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl shadow-lg p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-emerald-800 flex items-center"><ShieldCheckIcon className="h-6 w-6 text-emerald-600 mr-2"/>Plastic Credit Hub</h3>
                <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Live Data</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/70 backdrop-blur rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-1">{totals.plasticKg.toLocaleString()}</div>
                  <div className="text-xs text-emerald-700 font-medium uppercase tracking-wide">kg Plastic Recycled</div>
                </div>
                <div className="bg-white/70 backdrop-blur rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">{totals.credits.toLocaleString()}</div>
                  <div className="text-xs text-primary-700 font-medium uppercase tracking-wide">Plastic Credits</div>
                </div>
              </div>
              <div className="bg-white/50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Credit Conversion Rate</span>
                  <span className="text-lg font-bold text-emerald-600">1:1</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full" style={{width: '100%'}}></div>
                </div>
                <div className="text-xs text-gray-600">1 kg plastic = 1 plastic credit • Other materials: {(totals.totalKg - totals.plasticKg).toLocaleString()} kg tracked</div>
              </div>
            </div>
            
            {/* Top Districts - Visual Ranking */}
            <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center"><MapPinIcon className="h-6 w-6 text-primary-600 mr-2"/>District Performance Rankings</h3>
                <div className="text-sm text-gray-500">{districts.length} districts active</div>
              </div>
              <div className="space-y-4">
                {districts.slice(0, 5).map((d, index) => {
                  const maxPlastic = Math.max(...districts.map(dist => dist.plasticKg))
                  const percentage = maxPlastic > 0 ? (d.plasticKg / maxPlastic) * 100 : 0
                  const rankColors = ['bg-gradient-to-r from-yellow-400 to-orange-500', 'bg-gradient-to-r from-gray-400 to-gray-500', 'bg-gradient-to-r from-primary-400 to-primary-600', 'bg-gradient-to-r from-blue-500 to-indigo-500', 'bg-gradient-to-r from-purple-500 to-pink-500']
                  return (
                    <div key={d.district} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${rankColors[index] || 'bg-gray-400'}`}>
                        #{index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-gray-800">{d.district}</h4>
                          <div className="text-right">
                            <div className="text-lg font-bold text-primary-600">{d.plasticKg} kg</div>
                            <div className="text-xs text-emerald-600 font-medium">{d.credits} PC</div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div className="bg-gradient-to-r from-primary-400 to-primary-600 h-2 rounded-full transition-all duration-500" style={{width: `${percentage}%`}}></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>Cans: {d.aluminiumKg}kg • Paper: {d.paperKg}kg • Glass: {d.glassKg}kg</span>
                          <span>{percentage.toFixed(1)}% of top performer</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Brand Performance Ranking */}
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <TrophyIcon className="h-5 w-5 mr-2 text-primary-600" />
                Brand Return Performance Ranking
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {brandPerformanceData.map((brand, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mr-4 ${
                          index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                          index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500' :
                          index === 2 ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 
                          'bg-gradient-to-r from-primary-500 to-primary-600'
                        }`}>
                          #{index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 text-lg">{brand.brand}</h4>
                          <p className="text-sm text-gray-500">Daily Average: {brand.avg_daily.toLocaleString()} bottles</p>
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <p className="text-2xl font-bold text-primary-600">{brand.total_bottles.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">{brand.percentage}% of total</p>
                        <p className={`text-sm font-medium ${
                          brand.growth.startsWith('+') ? 'text-green-600' : 'text-red-600'
                        }`}>{brand.growth}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm">
                      <div>
                        <span className="text-gray-500">Popular Sizes: </span>
                        <span className="font-medium">{brand.popular_sizes.join(', ')}</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {brand.popular_sizes.slice(0, 3).map((size, sizeIndex) => (
                          <span key={sizeIndex} className={`px-2 py-1 text-xs rounded-full ${
                            sizeIndex === 0 ? 'bg-primary-100 text-primary-800' :
                            sizeIndex === 1 ? 'bg-primary-100 text-primary-800' :
                            'bg-primary-100 text-primary-800'
                          }`}>
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Enhanced Plastic Credit Ledger */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-base sm:text-xl font-bold text-white flex items-center whitespace-nowrap">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Plastic Credit Transaction Ledger
                </h3>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full text-sm font-medium">
                    {ledgerSummary.totalCredits.toLocaleString()} Chain Credits
                  </div>
                  <div className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-sm font-medium">
                    {ledgerSummary.totalRecords.toLocaleString()} Records
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <CarbonCreditLedger onSummaryChange={setLedgerSummary} />
            </div>
          </div>

          {/* System Management */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">System Administration</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-primary-50 p-4 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer">
                  <h4 className="font-medium text-primary-900">User Management</h4>
                  <p className="text-sm text-primary-700 mt-1">Manage system users and permissions</p>
                </div>
                <div className="bg-primary-50 p-4 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer">
                  <h4 className="font-medium text-primary-900">Data Integrity</h4>
                  <p className="text-sm text-primary-700 mt-1">Monitor and verify data integrity</p>
                </div>
                <div className="bg-primary-50 p-4 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer">
                  <h4 className="font-medium text-primary-900">RVM Network</h4>
                  <p className="text-sm text-primary-700 mt-1">Manage RVM network and maintenance</p>
                </div>
                <div className="bg-primary-50 p-4 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer">
                  <h4 className="font-medium text-primary-900">Security Logs</h4>
                  <p className="text-sm text-primary-700 mt-1">Access and security monitoring</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default AdminPortal
